import { Options } from 'pusher-js';
import { Item } from '@/models/menu';
import { t } from '@/services/locale.service';
import { Theme } from '@/services/theme.service';
/**
 * All configurable options must be defined in the Config interface
 */
export interface Config {
    theme: Theme;
    api: {
        host: string;
    };
    home: {
        header: {
            image: string;
        };
    };
    aside: {
        defaultMenu: Item[];
    };
    documentSign: {
        queue: boolean;
        pusher: {
            enabled: boolean;
            key: string;
            options: Options;
        };
    };
}

/**
 * Configurations are based on the API hostname
 */
const CUSTOMIZABLE_CLIENT_DOMAINS: Record<string, () => Config> = {
    localhost: (): Config => devConfig(),
    'dev.isiparts.com': (): Config => devConfig(),
    'comabona.isiparts.com': (): Config => comabonaConfig(),
    'millarto.isiparts.com': (): Config => millartoConfig(),
    'isiparts.frain.es': (): Config => frainConfig(),
};

export const DEFAULT_SAT_MENU: Item[] = [
    {
        id: 1,
        text: t('MENU.sat'),
        icon: '',
        childs: [
            {
                id: 2,
                text: t('WORKORDERS.work_order', 2),
                icon: 'folder',
                childs: [{ id: 3, text: t('WORKORDERS.work_order_abbr', 2), icon: '', route: 'or/list' }],
            },
        ],
    },
];

export const DEFAULT_COMERCIAL_MENU: Item[] = [
    {
        id: 4,
        text: t('MENU.comercial'),
        icon: 'group',
        childs: [
            {
                id: 5,
                text: t('MENU.prospect', 2),
                icon: 'folder',
                childs: [
                    { id: 6, text: t('MENU.prospect_new', 2), icon: 'group', route: 'potencial/form' },
                    { id: 7, text: t('MENU.prospect_edit', 2), icon: 'group', route: 'potencial/list' },
                ],
            },
        ],
    },
];

export const defaultApiHost = (): string => {
    let host = 'https://dev-api.isiparts.com:8443';

    const locationRegex = window.location.hostname.match(/^(.*?)\.isiparts\.com$/i);

    if (locationRegex !== null && locationRegex.length > 1) {
        host = `https://${locationRegex[1]}-api.isiparts.com:8443`;
    }

    return host;
};

/**
 * Load configuration based on the current API hostname
 */
export const loadConfig = (customHostname?: string): Config => {
    const hostname = customHostname || location.hostname;

    console.log('hostname', hostname);

    if (hostname in CUSTOMIZABLE_CLIENT_DOMAINS) {
        return CUSTOMIZABLE_CLIENT_DOMAINS[hostname]();
    }

    return defaultConfig();
};

/**
 * This configuration applies to all clients unless overriden
 */
const defaultConfig = (): Config => {
    return {
        theme: 'theme-isiparts',
        api: {
            host: defaultApiHost(),
        },
        home: {
            header: {
                image: 'https://res.cloudinary.com/dygtknehl/image/upload/f_auto,q_auto:best/ISIParts_20logo_20home_prqswp',
            },
        },
        aside: {
            defaultMenu: location.hostname.includes('isiparts.pages.dev')
                ? [...DEFAULT_COMERCIAL_MENU, ...DEFAULT_SAT_MENU]
                : [],
        },
        documentSign: {
            queue: false,
            pusher: {
                enabled: false,
                key: '',
                options: {
                    cluster: 'eu',
                },
            },
        },
    };
};

/**
 * Override default configuration for dev-api.isiparts.com
 */
const devConfig = (): Config => {
    const config = defaultConfig();
    config.home.header.image =
        'https://res.cloudinary.com/dygtknehl/image/upload/co_rgb:ed772d,l_text:open%20sans_48_bold_normal_left:DEV/fl_layer_apply,g_south_east,x_24,y_24/ISIParts_20logo_20home_prqswp';
    config.aside.defaultMenu = [...DEFAULT_COMERCIAL_MENU, ...DEFAULT_SAT_MENU];

    config.documentSign.pusher.enabled = true;
    config.documentSign.pusher.key = '8024737cfc5fca6be712';

    return config;
};

/**
 * Override default configuration for comabona-api.isiparts.com
 */
const comabonaConfig = (): Config => {
    const config = defaultConfig();
    config.aside.defaultMenu = DEFAULT_SAT_MENU;

    return config;
};

/**
 * Override default configuration for millarto-api.isiparts.com
 */
const millartoConfig = (): Config => {
    const config = defaultConfig();
    config.aside.defaultMenu = DEFAULT_COMERCIAL_MENU;

    return config;
};

/**
 * Override default configuration for Frain
 */
const frainConfig = (): Config => {
    const config = defaultConfig();
    config.api.host = 'https://api.frain.es:8443';
    config.aside.defaultMenu = [...DEFAULT_COMERCIAL_MENU, ...DEFAULT_SAT_MENU];

    return config;
};
